import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Key, CheckCircle, Shield } from 'react-feather';
import config from '../../../config';

import ReportShell from '../../Admin/Reports/shared/ReportShell';
import '../tenant-portal.css';

const CHARSET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const randomKey = (len) => Array.from({ length: len }, () => CHARSET[Math.floor(Math.random() * CHARSET.length)]).join('');

const ManageAPIKey = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('sessiontoken');
    const login_id = localStorage.getItem('loginId');
    const tenantId = localStorage.getItem('tenantId');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [apiKey, setApiKey] = useState('');
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        axios.get(`${config.hostname}/tenant/${tenantId}`, { headers })
            .then((res) => { setApiKey(res.data.tenant_api_key || ''); setEmail(res.data.tenant_email_id || ''); })
            .catch((err) => console.error('api key load failed', err));
    }, [tenantId, headers]);

    const hasKey = apiKey && apiKey !== 'null';

    // Generate a key that doesn't collide, then persist it.
    const generate = async () => {
        setBusy(true);
        try {
            let key = randomKey(21);
            for (let i = 0; i < 5; i++) {
                const res = await axios.get(`${config.hostname}/tenant/apikey/${key}`, { headers });
                if (!res.data.exists) break;
                key = randomKey(21);
            }
            await axios.post(`${config.hostname}/tenant/insertapikey`, { api_key: key, email_id: email, login_id }, { headers });
            toast.success(t('Successfully updated'));
            setApiKey(key); // flips the card to the "Generated" state
        } catch (err) {
            console.error('api key generate failed', err);
            toast.error(t('Error'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Fragment>
            <ReportShell
                crumbRoot="Tenant Portal"
                crumb="API Key"
                title={t('Sales API Key')}
                subtitle={t('Push real-time sales from your POS using the Sales API.')}
            >
                <div className="v3-card v3-apikey-card">
                    <div className="v3-apikey-status">
                        <span className={`v3-apikey-icon ${hasKey ? 'ok' : ''}`}>
                            {hasKey ? <CheckCircle size={20} /> : <Key size={20} />}
                        </span>
                        <div>
                            <h3 className="v3-title">{t('Sales API')}</h3>
                            <p className="v3-body" style={{ margin: 0, color: 'var(--v3-text-muted)' }}>
                                {hasKey
                                    ? t('An API key is active for this account.')
                                    : t('No API key yet. Generate one to start pushing sales.')}
                            </p>
                        </div>
                        <div className="v3-apikey-action">
                            {hasKey ? (
                                <span className="v3-apikey-generated"><CheckCircle size={14} /> {t('Generated')}</span>
                            ) : (
                                <button type="button" className="v3-btn v3-btn-primary" onClick={generate} disabled={busy}>
                                    <Key size={14} /> {busy ? t('Generating…') : t('Generate API key')}
                                </button>
                            )}
                        </div>
                    </div>

                    {hasKey && (
                        <p className="v3-apikey-note">
                            <Shield size={13} /> {t('For security the key is shown only once at creation and is not displayed again. Contact your administrator if you need it reissued.')}
                        </p>
                    )}
                </div>

                <div className="v3-card v3-apikey-card">
                    <h3 className="v3-title">{t('Integration')}</h3>
                    <p className="v3-body" style={{ color: 'var(--v3-text-secondary)' }}>
                        {t('Authenticate each request with your key in the')} <code>x-api-key</code> {t('header.')}
                    </p>
                    <div className="v3-apikey-sample">
                        <span className="v3-label">{t('Payload sample')}</span>
                        <pre>{`{
  "concessionaire_code": "...",
  "tenant_code": "...",
  "outlet_code": "...",
  "mall_code": "...",
  "transaction_date": "2026-05-02T00:00:00Z",
  "transaction_count": 20,
  "net_sales": 3200
}`}</pre>
                        <span className="v3-label">{t('F&B payload adds per-partner splits')}</span>
                        <pre>{`{ ..., "dinein_count": 5, "dinein_sales": 1000, "talabat_count": 5, "talabat_sales": 1000 }`}</pre>
                    </div>
                </div>
            </ReportShell>
        </Fragment>
    );
};

export default ManageAPIKey;
